import { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { Pencil, Search as SearchIcon, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { searchAdminQaItems } from '@/lib/api/admin';

function scoreTone(score) {
  if (score >= 0.75) return 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30';
  if (score >= 0.6) return 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30';
  return 'bg-rose-500/20 text-rose-700 dark:text-rose-300 border-rose-500/30';
}

function hitToItem(hit) {
  return {
    id: hit.id,
    section: hit.section,
    question: hit.question,
    answer: hit.answer,
    aliases: hit.aliases,
    metadata: hit.metadata || {},
  };
}

function QaSearchDialog({ open, onOpenChange, onEditRequest, onDeleteRequest }) {
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(10);
  const [isSearching, setIsSearching] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const requestId = useRef(0);

  useEffect(() => {
    if (open) {
      setError(null);
      // Defer to ensure the dialog mounted before focusing.
      const t = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(t);
    }
    setResults(null);
    setQuery('');
  }, [open]);

  const runSearch = async (text, lim) => {
    const trimmed = String(text || '').trim();
    if (!trimmed) {
      setResults(null);
      return;
    }
    const ticket = ++requestId.current;
    setIsSearching(true);
    setError(null);
    try {
      const payload = await searchAdminQaItems({ query: trimmed, limit: lim });
      if (ticket !== requestId.current) return; // stale
      setResults(payload);
    } catch (err) {
      if (ticket !== requestId.current) return;
      setError(err?.message || 'Search failed');
      setResults(null);
    } finally {
      if (ticket === requestId.current) {
        setIsSearching(false);
      }
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    if (!query.trim()) {
      setResults(null);
      return undefined;
    }
    const handle = window.setTimeout(() => {
      runSearch(query, limit);
    }, 220);
    return () => window.clearTimeout(handle);
  }, [open, query, limit]);

  const handleSubmit = (event) => {
    event.preventDefault();
    runSearch(query, limit);
  };

  const items = useMemo(() => (Array.isArray(results?.items) ? results.items : []), [results]);

  const handleDeleteHit = async (hit) => {
    if (!onDeleteRequest) return;
    setDeletingId(hit.id);
    try {
      const removed = await onDeleteRequest(hitToItem(hit));
      if (removed) {
        setResults((prev) => {
          if (!prev || !Array.isArray(prev.items)) return prev;
          return { ...prev, items: prev.items.filter((i) => i.id !== hit.id) };
        });
      }
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(92vh,860px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <DialogHeader className="shrink-0 space-y-1.5 px-6 pt-6">
          <DialogTitle className="flex items-center gap-2">
            <SearchIcon className="h-5 w-5" />
            Test a message
          </DialogTitle>
          <DialogDescription>
            Embeds a patient message with <span className="font-mono">{results?.embeddingModel || 'OPENAI_EMBEDDING_MODEL'}</span> and
            ranks your curated phrases in <span className="font-mono">{results?.collection || 'QA_QDRANT_COLLECTION'}</span>{' '}
            by similarity — a preview of what the bot would match.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex shrink-0 flex-wrap items-center gap-2 px-6 pt-4">
          <Input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="e.g. i lost my glasses"
            className="flex-1 min-w-[20ch]"
          />
          <Input
            type="number"
            value={limit}
            onChange={(event) => setLimit(Math.max(1, Math.min(50, Number(event.target.value) || 10)))}
            min={1}
            max={50}
            className="w-24"
            aria-label="Result limit"
          />
          <Button type="submit" disabled={!query.trim() || isSearching}>
            {isSearching ? <Spinner className="mr-2 h-4 w-4" /> : <SearchIcon className="mr-2 h-4 w-4" />}
            Test
          </Button>
        </form>

        <p className="shrink-0 px-6 pt-2 text-xs text-muted-foreground">
          Phrases scoring above the configured threshold (~0.80) trigger an auto-reply, so use this to sanity-check
          coverage — a green score here would short-circuit the normal flow with that curated answer.
        </p>

        {error && (
          <div className="mx-6 mt-4 shrink-0 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <ScrollArea className="h-[min(70vh,640px)] min-h-0 w-full px-6 pb-6 pt-4">
          {!query.trim() && !results && (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Type a message to see how the assistant bot would rank curated phrases for it.
            </div>
          )}

          {query.trim() && !isSearching && items.length === 0 && results && (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No matches — the collection might be empty, or no phrases scored above the noise floor.
            </div>
          )}

          <ul className="space-y-3 py-1">
            {items.map((hit) => {
              const primary = hit.question || hit.aliases?.[0] || '—';
              const extraMatches = (Array.isArray(hit.matches) ? hit.matches : []).filter(
                (m) => String(m.phrase || '').trim() !== String(primary).trim(),
              );
              return (
                <li key={hit.id} className="rounded-lg border border-border bg-card p-4 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={cn('inline-flex items-center rounded border px-2 py-0.5 text-xs font-mono', scoreTone(hit.topScore))}>
                          {hit.topScore.toFixed(3)}
                        </span>
                        <Badge variant="outline">{hit.section || 'Uncategorised'}</Badge>
                      </div>
                      <p className="text-sm font-semibold leading-snug">{primary}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => onEditRequest?.(hitToItem(hit))}
                      >
                        <Pencil className="mr-2 h-3.5 w-3.5" />
                        Edit
                      </Button>
                      {onDeleteRequest && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          disabled={deletingId === hit.id}
                          onClick={() => handleDeleteHit(hit)}
                        >
                          {deletingId === hit.id ? (
                            <Spinner className="mr-2 h-3.5 w-3.5" />
                          ) : (
                            <Trash2 className="mr-2 h-3.5 w-3.5" />
                          )}
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>

                  {extraMatches.length > 0 && (
                    <div className="mt-2 space-y-1 rounded-md bg-muted/40 px-3 py-2 text-xs">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Other matching triggers
                      </p>
                      <ul className="space-y-1">
                        {extraMatches.map((m, idx) => (
                          <li key={idx} className="flex items-baseline gap-2">
                            <span className="font-mono text-[11px] text-muted-foreground">{m.score.toFixed(3)}</span>
                            <Badge variant="secondary" className="text-[10px] capitalize">
                              {m.kind === 'question' ? 'trigger' : 'alt'}
                            </Badge>
                            <span className="truncate">{m.phrase}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {hit.answer && (
                    <div className="prose prose-sm dark:prose-invert mt-3 max-w-none text-sm">
                      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{hit.answer}</ReactMarkdown>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export default QaSearchDialog;
